import { useCallback, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { Badge, Button, Text, Tooltip } from "@marekl/adminer/components";
import { useOnlineStatus } from "@marekl/adminer/hooks";
import { dayjs } from "@marekl/adminer/datetime";
import { useNow } from "../../lib/useNow";
import { useLastUpdatedAt } from "../../lib/useLastUpdatedAt";
import styles from "./RefreshButton.module.css";

export interface RefreshButtonProps {
  /**
   * Query-key prefixes covering everything this page shows. The page hands
   * over prefixes rather than its own `refetch` so that one click reaches
   * every query on screen: the Workers page has two tables, and the Messages
   * list has a detail modal that opens over it.
   */
  queryKeys: QueryKey[];
  /** The page is auto-refreshing right now — shows the "Live" tag. */
  live?: boolean;
}

/**
 * The page header's freshness group: how old the data is, whether it is
 * keeping itself current, and a way to ask for it again now.
 *
 * No keyboard shortcut on purpose. Every list page is filter-heavy (inputs,
 * autocompletes, date pickers, CodeMirror on the template editor), so a
 * bare-letter binding would need an "is the user typing?" guard, and the two
 * chords anyone would actually reach for belong to the browser — where they
 * already do the right thing here, since filters, cursor and limit all live
 * in the URL.
 */
export function RefreshButton({ queryKeys, live = false }: RefreshButtonProps) {
  const qc = useQueryClient();
  const online = useOnlineStatus();
  const updatedAt = useLastUpdatedAt(queryKeys);
  const [busy, setBusy] = useState(false);

  // `refetchQueries` defaults to cancelRefetch, so a click lands on top of an
  // in-flight poll instead of queueing behind it. It also re-runs a query
  // sitting in error, which makes this a second, always-present retry next to
  // the table's own.
  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      await Promise.all(
        queryKeys.map((queryKey) => qc.refetchQueries({ queryKey, type: "active" })),
      );
    } finally {
      setBusy(false);
    }
  }, [qc, queryKeys]);

  // Seconds, deliberately — `formatDateTime` is configured to the minute, and
  // a freshness reading without seconds says nothing.
  const tip = updatedAt
    ? `Refresh · last updated ${dayjs(updatedAt).format("HH:mm:ss")}` +
      (live ? " · auto-refreshing" : "")
    : "Refresh";

  return (
    <span className={styles.root}>
      {live && (
        <Badge weight="ghost" variant="ok" dot>
          Live
        </Badge>
      )}
      {updatedAt !== undefined && <Stamp at={updatedAt} />}
      <Tooltip content={tip} placement="bottom">
        <Button
          variant="neutral"
          weight="ghost"
          iconOnly
          startIcon={<RefreshCw aria-hidden="true" />}
          loading={busy}
          disabled={!online}
          aria-label="Refresh"
          onClick={() => void refresh()}
        />
      </Tooltip>
    </span>
  );
}

/**
 * Its own component so the one-second tick re-renders a text node and nothing
 * else — the button beside it keeps its focus and its pressed state.
 *
 * `loading` on the button is deliberately not wired to background fetching:
 * it implies `disabled`, so a poll would make the control un-clickable for a
 * moment out of every cycle and blur a focused button out from under a
 * keyboard user. The button reports what the user asked for; the stamp
 * reports what the data is doing.
 */
function Stamp({ at }: { at: number }) {
  const now = useNow(1_000);
  return (
    <Text variant="muted" className={styles.stamp}>
      {formatAgo(at, now)}
    </Text>
  );
}

/**
 * Terser than the lib's `formatRelative`, which is `dayjs().fromNow()` and
 * rounds everything under 45 seconds to "a few seconds ago" — a stamp that
 * cannot tick is not a stamp.
 */
function formatAgo(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return dayjs(at).format("HH:mm");
}
